import React from "react";
import { Link } from "react-router-dom";
import {
  Truck, MoveHorizontal, TowerControl, Container, PackageOpen,
  Layers, Zap, ArrowUpNarrowWide, ArrowRight, Wrench
} from "lucide-react";
import SplitPageHero from "../components/SplitPageHero";
import Reveal from "../components/Reveal";
import { Button } from "../components/Button";
import { FLEET, FLEET_SUPPORT, IMAGES } from "../data";

const ICONS = {
  Truck, MoveHorizontal, TowerControl, Container, PackageOpen, Layers, Zap,
  ArrowUpNarrowWide, Crane: Truck,
};

export default function Fleet() {
  return (
    <div data-testid="fleet-page">
      <SplitPageHero
        eyebrow="Our Equipment"
        titleGreen="Purpose-built"
        titleOrange="for heavy haulage."
        subtitle="A comprehensive fleet of specialised trailers, prime movers and lifting equipment — engineered for the heaviest, longest and most complex ODC movements."
        imageWebp="/hero-fleet.webp"
        imagePng="/hero-fleet.png"
        imageAlt="MRL Transports green hydraulic modular axle trailer at sunset"
        badgeLabel="Modular Axles"
        badgeSub="Hydraulic Fleet"
      />

      {/* Feature banner */}
      <section className="py-16 md:py-24 bg-white" data-testid="fleet-intro">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="aspect-[16/10] overflow-hidden bg-brand-smoke">
                <img
                  src={IMAGES.fleetBlade}
                  alt="Blade transportation trailer"
                  className="w-full h-full object-cover"
                />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal><p className="section-tag">Fleet Overview</p></Reveal>
            <Reveal delay={100}>
              <h2 className="font-heading text-4xl md:text-5xl leading-[0.95] tracking-tightest mt-6 text-brand-ink">
                Every load,<br />handled with precision.
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <p className="font-body text-lg text-zinc-700 leading-relaxed mt-6">
                Our fleet is maintained to the highest operational standards to ensure safety, reliability and
                efficiency for every project. From modular hydraulic axles to telescopic blade trailers — we bring
                the right equipment to the right site, on time.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FLEET CATEGORIES */}
      {FLEET.map((cat, ci) => (
        <section key={cat.category} className={ci % 2 === 0 ? "py-24 bg-brand-smoke" : "py-24 bg-white"} data-testid={`fleet-category-${ci}`}>
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="flex flex-wrap items-end justify-between mb-12 gap-4">
              <div>
                <Reveal><p className="section-tag">Category {String(ci + 1).padStart(2, "0")}</p></Reveal>
                <Reveal delay={100}>
                  <h2 className="font-heading text-3xl md:text-5xl leading-[0.95] tracking-tightest mt-4 text-brand-ink">
                    {cat.category}
                  </h2>
                </Reveal>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cat.items.map((f, i) => {
                const Icon = ICONS[f.icon] || Truck;
                return (
                  <Reveal key={f.name} delay={i * 80}>
                    <div
                      className="group bg-white border-2 border-zinc-200 hover:border-brand-orange p-8 h-full transition-all hover:-translate-y-1"
                      data-testid={`fleet-item-${ci}-${i}`}
                    >
                      <div className="w-14 h-14 flex items-center justify-center border-2 border-brand-green group-hover:bg-brand-green transition-colors">
                        <Icon className="w-6 h-6 text-brand-green group-hover:text-white" />
                      </div>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-brand-orange mt-6">Unit {String(i + 1).padStart(2, "0")}</p>
                      <h3 className="font-heading text-xl md:text-2xl mt-2 tracking-tight text-brand-ink">{f.name}</h3>
                      <p className="font-body text-sm text-zinc-600 mt-3">{f.spec}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      {/* SUPPORT INFRASTRUCTURE */}
      <section className="py-24 md:py-32 bg-brand-ink text-white" data-testid="fleet-support">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-6">
              <Reveal><p className="section-tag text-brand-orange">Support Infrastructure</p></Reveal>
              <Reveal delay={100}>
                <h2 className="font-heading text-4xl md:text-6xl leading-[0.95] tracking-tightest mt-6">
                  Beyond the trailer.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:pt-8">
              <Reveal delay={200}>
                <p className="font-body text-lg text-zinc-300 leading-relaxed">
                  Every transit is backed by a robust support ecosystem — escort vehicles, maintenance campers,
                  live GPS tracking and a dedicated operations desk.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {FLEET_SUPPORT.map((s, i) => (
              <Reveal key={s} delay={i * 80}>
                <div
                  className="bg-white/5 backdrop-blur border-2 border-white/20 hover:border-brand-orange p-6 min-h-[140px] flex items-center gap-4 transition-colors"
                  data-testid={`fleet-support-${i}`}
                >
                  <Wrench className="w-6 h-6 text-brand-orange shrink-0" />
                  <p className="font-heading text-lg tracking-tight">{s}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-brand-orange text-white" data-testid="fleet-cta">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-heading text-3xl md:text-5xl leading-[0.95] tracking-tightest">
              Need a specific fleet configuration for your project?
            </h2>
            <p className="font-body text-lg mt-4 text-white/90 max-w-2xl">
              Share load dimensions, weights and destination — we'll propose the right combination of equipment.
            </p>
          </div>
          <div className="lg:col-span-4">
            <Link to="/contact" data-testid="fleet-cta-contact">
              <Button variant="primary" size="lg" className="w-full">Request Fleet Availability <ArrowRight className="w-4 h-4" /></Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
